import React, { useEffect, useRef, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Checkbox,
    Grid,
    Paper,
    Typography,
    Divider,
    Switch,
    IconButton,
    Tooltip,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Avatar,
    useTheme,
    Autocomplete,
    Chip,
    Fade,
    Link,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import Crop from "@mui/icons-material/Crop";
import AddPhotoAlternateOutlined from "@mui/icons-material/AddPhotoAlternateOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useAuth } from "../hooks/useAuth";
import { allowOrWarn, Util } from "../util/Util";
import { canRead, canUpdate } from "../util/access";
import { toast } from "react-toastify";
import { LOCAL_STORAGE_KEY_LABELS, MYSQL_LIMITS } from "../util/constants";
import { pendingTaskKeyLabelBase } from "../util/pendingTaskStorage";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import { ArrowBack, CancelOutlined, Check, Logout } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useUI } from "../providers/MoleUIContext";
import { ColorPicker } from "../components/ColorPicker";
import { useTranslation } from "react-i18next";

const CATEGORIES = {
    BIZ: "business",
    UI: "userInterface",
    SEC: "security",
    DEFAULT: "system",
};

type SettingTypeDescriptor = {
    type: string;
    options?: string[];
};

// App Info
const APP_VERSION = "v2.4.1";
const COPYRIGHT_YEAR = new Date().getFullYear();

type IndexedDbRecordItem = {
    keyLabel: string;
    rawKey: IDBValidKey;
    size: number;
    selected: boolean;
};

type IndexedDbStoreItem = {
    name: string;
    size: number;
    selected: boolean;
    expanded: boolean;
    recordsLoading: boolean;
    recordsLoaded: boolean;
    records: IndexedDbRecordItem[];
};

type IndexedDbItem = { name: string; size: number; selected: boolean; expanded: boolean; stores: IndexedDbStoreItem[] };

type DeleteConfirmAction = "local-all" | "local-selected" | "indexed-all" | "indexed-selected" | "indexed-stores" | "indexed-records";

type DeleteConfirmState = {
    open: boolean;
    action: DeleteConfirmAction | null;
    title: string;
    message: string;
};

const MAX_RECORDS_PER_STORE = 500;

const AdminSettings = () => {
    const [openDialog, setOpenDialog] = useState(false);
    const [editItem, setEditItem] = useState<any | null>(null);
    const [editValue, setEditValue] = useState("");
    const [cropperData, setCropperData] = useState<string | null>(null);
    const [cropperRef, setCropperRef] = useState<Cropper | null>(null);
    const [storageLoading, setStorageLoading] = useState(false);
    const [localStorageBytes, setLocalStorageBytes] = useState(0);
    const [indexedDbBytes, setIndexedDbBytes] = useState<number | null>(null);
    const [localStorageItems, setLocalStorageItems] = useState<Array<{ key: string; label: string; size: number; selected: boolean }>>([]);
    const [indexedDbItems, setIndexedDbItems] = useState<IndexedDbItem[]>([]);
    const [expandedStorage, setExpandedStorage] = useState({ local: false, indexed: false });
    const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>({
        open: false,
        action: null,
        title: "",
        message: "",
    });
    const [deleteConfirmText, setDeleteConfirmText] = useState("");
    const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);
    const [fullSettings, setFullSettings] = useState<any>(null);
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [settingsLoadFailed, setSettingsLoadFailed] = useState(false);

    const { session, access, accessVersion, api, logout } = useAuth();
    const { loginModalOpen, queueReRun } = useUI();
    const theme = useTheme();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const getSettingType = (item: any) => {
        const descriptor = item?.type as SettingTypeDescriptor | undefined;
        return descriptor?.type || "";
    };

    const getSettingEnumOptions = (item: any) => {
        const descriptor = item?.type as SettingTypeDescriptor | undefined;
        return Array.isArray(descriptor?.options) ? descriptor.options : [];
    };
    const deleteKeyword = t("common:settings.storage.confirmDelete.deleteKeyword");
    const localizedStorageKeyLabels = t("common:settings.storage.keyLabels", { returnObjects: true }) as Record<string, string>;

    const toastIdRef = useRef<string | number>(-1);

    const dangerOutlineButtonSx = {
        borderColor: "error.main",
        color: "error.main",
        "&:hover": {
            borderColor: "error.dark",
            backgroundColor: "error.main",
            color: "error.contrastText",
        },
    };

    const dangerContainedButtonSx = {
        backgroundColor: "error.main",
        color: "error.contrastText",
        "&:hover": {
            backgroundColor: "error.dark",
        },
    };


    const grouped = Object.entries(fullSettings || {}).reduce((acc, [key, config]) => {
        let category = CATEGORIES.DEFAULT;
        if (key.startsWith("BIZ_")) category = CATEGORIES.BIZ;
        if (key.startsWith("UI_")) category = CATEGORIES.UI;
        if (key.startsWith("SEC_")) category = CATEGORIES.SEC;

        if (!acc[category]) acc[category] = [];
        acc[category].push(config);
        return acc;
    }, {} as Record<string, any[]>);

    /** Derived during render so live permission sync updates disabled inputs immediately. */
    const canEdit = canUpdate(access, 'settings');

    const fetchInFlightRef = useRef(false);

    const fetchSettings = async () => {
        if (fetchInFlightRef.current) return;
        fetchInFlightRef.current = true;
        setSettingsLoading(true);
        setSettingsLoadFailed(false);

        try {
            const level = Number(session?.user_id) === 1 ? "full" : "";
            // Avoid trailing slash-only path quirks: full_settings vs full_settings/
            const path = level ? `full_settings/${level}` : "full_settings";
            const res = await api.get(path);

            if (res?.status === 200) {
                setFullSettings(res.data);
            } else if (res?.status === 401 || res?.status === 422) {
                // Session expired / rotated — queue for after re-auth modal succeeds.
                queueReRun(fetchSettings);
            } else {
                setSettingsLoadFailed(true);
                console.error("Failed to fetch data:", res);
                toast.error(t("common:toast.error.fetchDataFailed"));
            }
        } catch (err) {
            // Interceptor should fulfill 401s; this is a safety net against Uncaught.
            console.error("full_settings request failed:", err);
            setSettingsLoadFailed(true);
            toast.error(t("common:toast.error.fetchDataFailed"));
        } finally {
            setSettingsLoading(false);
            fetchInFlightRef.current = false;
        }
    };

    const formatBytes = (bytes: number) => {
        if (bytes === 0) return "0 B";
        const units = ["B", "KB", "MB", "GB"];
        const power = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
        const value = bytes / 1024 ** power;
        return `${value.toFixed(value >= 10 || power === 0 ? 0 : 1)} ${units[power]}`;
    };

    const keyToFriendlyLabel = (key: string) => {
        if (localizedStorageKeyLabels?.[key]) return localizedStorageKeyLabels[key];
        if (LOCAL_STORAGE_KEY_LABELS[key]) return LOCAL_STORAGE_KEY_LABELS[key];
        const scopedBase = pendingTaskKeyLabelBase(key);
        if (scopedBase !== key && LOCAL_STORAGE_KEY_LABELS[scopedBase]) {
            return LOCAL_STORAGE_KEY_LABELS[scopedBase];
        }
        // fallback: snake_case / camelCase → Title Case
        return key
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            .replace(/[_\-]+/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());
    };

    const getLocalStorageItems = () => {
        const items: Array<{ key: string; label: string; size: number }> = [];
        for (let i = 0; i < localStorage.length; i += 1) {
            const key = localStorage.key(i);
            if (!key) continue;
            const value = localStorage.getItem(key) || "";
            items.push({ key, label: keyToFriendlyLabel(key), size: (key.length + value.length) * 2 });
        }
        return items.sort((a, b) => b.size - a.size);
    };

    const estimateValueBytes = (value: unknown) => {
        try {
            const json = JSON.stringify(value);
            if (typeof json !== "string") return 0;
            return new Blob([json]).size;
        } catch {
            return 0;
        }
    };

    const estimateStoreBytes = (db: IDBDatabase, storeName: string) =>
        new Promise<number>((resolve) => {
            let total = 0;
            const tx = db.transaction(storeName, "readonly");
            const store = tx.objectStore(storeName);
            const req = store.openCursor();

            req.onsuccess = () => {
                const cursor = req.result;
                if (!cursor) {
                    resolve(total);
                    return;
                }

                total += estimateValueBytes(cursor.key);
                total += estimateValueBytes(cursor.value);
                cursor.continue();
            };

            req.onerror = () => resolve(total);
            tx.onerror = () => resolve(total);
            tx.onabort = () => resolve(total);
        });

    const keyToLabel = (key: IDBValidKey) => {
        if (key instanceof Date) {
            return key.toISOString();
        }
        if (Array.isArray(key)) {
            return JSON.stringify(key);
        }
        return String(key);
    };

    const openIndexedDb = (name: string) =>
        new Promise<IDBDatabase | null>((resolve) => {
            const openReq = indexedDB.open(name);

            openReq.onsuccess = () => resolve(openReq.result);
            openReq.onerror = () => resolve(null);
            openReq.onblocked = () => resolve(null);
        });

    const estimateIndexedDbBytesManual = async () => {
        const idbAny = indexedDB as IDBFactory & {
            databases?: () => Promise<Array<{ name?: string | null }>>;
        };

        if (!idbAny.databases) {
            return null;
        }

        const dbs = await idbAny.databases();
        const names = dbs.map((db) => db.name).filter((name): name is string => Boolean(name));

        const items: Array<{ name: string; size: number; stores: Array<{ name: string; size: number }> }> = [];

        for (const name of names) {
            const db = await openIndexedDb(name);

            if (!db) continue;

            let dbTotal = 0;
            const stores: Array<{ name: string; size: number }> = [];

            for (const storeName of Array.from(db.objectStoreNames)) {
                const storeSize = await estimateStoreBytes(db, storeName);
                stores.push({ name: storeName, size: storeSize });
                dbTotal += storeSize;
            }

            db.close();
            items.push({ name, size: dbTotal, stores: stores.sort((a, b) => b.size - a.size) });
        }

        const total = items.reduce((sum, item) => sum + item.size, 0);

        return {
            total,
            items: items.sort((a, b) => b.size - a.size),
        };
    };

    const mergeLocalSelections = (nextItems: Array<{ key: string; label: string; size: number }>) => {
        setLocalStorageItems((prev) => {
            const selectedMap = new Map(prev.map((item) => [item.key, item.selected]));
            return nextItems.map((item) => ({
                ...item,
                selected: selectedMap.get(item.key) || false,
            }));
        });
    };

    const mergeIndexedSelections = (nextItems: Array<{ name: string; size: number; stores: Array<{ name: string; size: number }> }>) => {
        setIndexedDbItems((prev) => {
            const previousMap = new Map(prev.map((item) => [item.name, item]));
            return nextItems.map((item) => ({
                name: item.name,
                size: item.size,
                selected: previousMap.get(item.name)?.selected || false,
                expanded: previousMap.get(item.name)?.expanded || false,
                stores: item.stores.map((store) => ({
                    name: store.name,
                    size: store.size,
                    selected: previousMap.get(item.name)?.stores.find((s) => s.name === store.name)?.selected || false,
                    expanded: false,
                    recordsLoading: false,
                    recordsLoaded: false,
                    records: [],
                })),
            }));
        });
    };

    const deleteIndexedDbByNames = async (names: string[]) => {
        await Promise.all(
            names.map(
                (name) =>
                    new Promise<void>((resolve, reject) => {
                        const req = indexedDB.deleteDatabase(name);
                        req.onsuccess = () => resolve();
                        req.onerror = () => reject(req.error);
                        req.onblocked = () => resolve();
                    })
            )
        );
    };

    const clearIndexedDbStores = async (dbName: string, storeNames: string[]) => {
        const db = await openIndexedDb(dbName);
        if (!db) {
            return;
        }

        await Promise.all(
            storeNames.map(
                (storeName) =>
                    new Promise<void>((resolve) => {
                        try {
                            const tx = db.transaction(storeName, "readwrite");
                            const req = tx.objectStore(storeName).clear();
                            req.onsuccess = () => resolve();
                            req.onerror = () => resolve();
                            tx.onabort = () => resolve();
                            tx.onerror = () => resolve();
                        } catch {
                            resolve();
                        }
                    })
            )
        );

        db.close();
    };

    const loadStoreRecords = async (dbName: string, storeName: string) => {
        const db = await openIndexedDb(dbName);
        if (!db) {
            return [] as IndexedDbRecordItem[];
        }

        const records = await new Promise<IndexedDbRecordItem[]>((resolve) => {
            const results: IndexedDbRecordItem[] = [];

            try {
                const tx = db.transaction(storeName, "readonly");
                const store = tx.objectStore(storeName);
                const req = store.openCursor();

                req.onsuccess = () => {
                    const cursor = req.result;
                    if (!cursor || results.length >= MAX_RECORDS_PER_STORE) {
                        resolve(results);
                        return;
                    }

                    results.push({
                        keyLabel: keyToFriendlyLabel(keyToLabel(cursor.key)),
                        rawKey: cursor.key,
                        size: estimateValueBytes(cursor.value),
                        selected: false,
                    });

                    cursor.continue();
                };

                req.onerror = () => resolve(results);
                tx.onabort = () => resolve(results);
                tx.onerror = () => resolve(results);
            } catch {
                resolve([]);
            }
        });

        db.close();
        return records;
    };

    const deleteIndexedDbRecords = async (dbName: string, storeName: string, keys: IDBValidKey[]) => {
        const db = await openIndexedDb(dbName);
        if (!db) {
            return;
        }

        await Promise.all(
            keys.map(
                (key) =>
                    new Promise<void>((resolve) => {
                        try {
                            const tx = db.transaction(storeName, "readwrite");
                            const req = tx.objectStore(storeName).delete(key);
                            req.onsuccess = () => resolve();
                            req.onerror = () => resolve();
                            tx.onabort = () => resolve();
                            tx.onerror = () => resolve();
                        } catch {
                            resolve();
                        }
                    })
            )
        );

        db.close();
    };

    const refreshStorageStats = async () => {
        setStorageLoading(true);

        try {
            const localItems = getLocalStorageItems();
            const localBytes = localItems.reduce((sum, item) => sum + item.size, 0);
            setLocalStorageBytes(localBytes);
            mergeLocalSelections(localItems);

            let idbUsage: number | null = null;
            if (navigator?.storage?.estimate) {
                const estimate = await navigator.storage.estimate();
                const usageDetails = (estimate as any)?.usageDetails;
                if (typeof usageDetails?.indexedDB === "number") {
                    idbUsage = usageDetails.indexedDB;
                }
            }

            const manualEstimate = await estimateIndexedDbBytesManual();
            mergeIndexedSelections(manualEstimate?.items || []);

            if (idbUsage === null) {
                idbUsage = manualEstimate?.total ?? null;
            }

            setIndexedDbBytes(idbUsage);
        } catch {
            setIndexedDbBytes(null);
        } finally {
            setStorageLoading(false);
        }
    };

    const clearLocalStorage = async () => {
        localStorage.clear();
        toast.success(t("common:settings.storage.pendingDataCleared"));
        await refreshStorageStats();
    };

    const clearSelectedLocalStorage = async () => {
        const selectedKeys = localStorageItems.filter((item) => item.selected).map((item) => item.key);
        if (!selectedKeys.length) {
            toast.info(t("common:settings.storage.selectAtLeastOnePending"));
            return;
        }

        selectedKeys.forEach((key) => localStorage.removeItem(key));
        toast.success(t("common:settings.storage.pendingItemsCleared", { count: selectedKeys.length }));
        await refreshStorageStats();
    };

    const clearIndexedDb = async () => {
        try {
            const idbAny = indexedDB as IDBFactory & {
                databases?: () => Promise<Array<{ name?: string | null }>>;
            };

            if (!idbAny.databases) {
                toast.error(t("common:settings.storage.cachedDataNotSupported"));
                return;
            }

            const dbs = await idbAny.databases();
            const names = dbs.map((db) => db.name).filter((name): name is string => Boolean(name));

            await deleteIndexedDbByNames(names);

            toast.success(t("common:settings.storage.cachedDataCleared"));
            await refreshStorageStats();
        } catch {
            toast.error(t("common:settings.storage.cachedDataClearFailed"));
        }
    };

    const clearSelectedIndexedDb = async () => {
        try {
            const names = indexedDbItems.filter((item) => item.selected).map((item) => item.name);
            if (!names.length) {
                toast.info(t("common:settings.storage.selectAtLeastOneCachedGroup"));
                return;
            }

            await deleteIndexedDbByNames(names);
            toast.success(t("common:settings.storage.cachedGroupsCleared", { count: names.length }));
            await refreshStorageStats();
        } catch {
            toast.error(t("common:settings.storage.cachedGroupsClearFailed"));
        }
    };

    const clearSelectedIndexedDbStores = async () => {
        try {
            const tasks = indexedDbItems
                .map((db) => ({
                    dbName: db.name,
                    storeNames: db.stores.filter((store) => store.selected).map((store) => store.name),
                }))
                .filter((entry) => entry.storeNames.length > 0);

            if (!tasks.length) {
                toast.info(t("common:settings.storage.selectAtLeastOneCachedSection"));
                return;
            }

            await Promise.all(tasks.map((task) => clearIndexedDbStores(task.dbName, task.storeNames)));
            const totalStores = tasks.reduce((sum, task) => sum + task.storeNames.length, 0);
            toast.success(t("common:settings.storage.cachedSectionsCleared", { count: totalStores }));
            await refreshStorageStats();
        } catch {
            toast.error(t("common:settings.storage.cachedSectionsClearFailed"));
        }
    };

    const clearSelectedIndexedDbRecords = async () => {
        try {
            const tasks = indexedDbItems
                .flatMap((db) =>
                    db.stores.map((store) => ({
                        dbName: db.name,
                        storeName: store.name,
                        keys: store.records.filter((record) => record.selected).map((record) => record.rawKey),
                    }))
                )
                .filter((entry) => entry.keys.length > 0);

            if (!tasks.length) {
                toast.info(t("common:settings.storage.selectAtLeastOneCachedRecord"));
                return;
            }

            await Promise.all(tasks.map((task) => deleteIndexedDbRecords(task.dbName, task.storeName, task.keys)));
            const totalRecords = tasks.reduce((sum, task) => sum + task.keys.length, 0);
            toast.success(t("common:settings.storage.cachedRecordsCleared", { count: totalRecords }));
            await refreshStorageStats();
        } catch {
            toast.error(t("common:settings.storage.cachedRecordsClearFailed"));
        }
    };

    const closeDeleteConfirm = () => {
        setDeleteConfirm({ open: false, action: null, title: "", message: "" });
        setDeleteConfirmText("");
        setDeleteConfirmLoading(false);
    };

    const openDeleteConfirm = (action: DeleteConfirmAction) => {
        const selectedLocalCount = localStorageItems.filter((item) => item.selected).length;
        const selectedDbCount = indexedDbItems.filter((item) => item.selected).length;
        const selectedStoreCount = indexedDbItems.reduce((sum, db) => sum + db.stores.filter((store) => store.selected).length, 0);
        const selectedRecordCount = indexedDbItems.reduce(
            (sum, db) => sum + db.stores.reduce((storeSum, store) => storeSum + store.records.filter((record) => record.selected).length, 0),
            0
        );

        const config: Record<DeleteConfirmAction, { title: string; message: string; blocked?: boolean }> = {
            "local-all": {
                title: t("common:settings.storage.confirmDelete.localAllTitle"),
                message: t("common:settings.storage.confirmDelete.localAllMessage"),
            },
            "local-selected": {
                title: t("common:settings.storage.confirmDelete.localSelectedTitle", { count: selectedLocalCount }),
                message: t("common:settings.storage.confirmDelete.localSelectedMessage"),
                blocked: selectedLocalCount === 0,
            },
            "indexed-all": {
                title: t("common:settings.storage.confirmDelete.indexedAllTitle"),
                message: t("common:settings.storage.confirmDelete.indexedAllMessage"),
            },
            "indexed-selected": {
                title: t("common:settings.storage.confirmDelete.indexedSelectedTitle", { count: selectedDbCount }),
                message: t("common:settings.storage.confirmDelete.indexedSelectedMessage"),
                blocked: selectedDbCount === 0,
            },
            "indexed-stores": {
                title: t("common:settings.storage.confirmDelete.indexedStoresTitle", { count: selectedStoreCount }),
                message: t("common:settings.storage.confirmDelete.indexedStoresMessage"),
                blocked: selectedStoreCount === 0,
            },
            "indexed-records": {
                title: t("common:settings.storage.confirmDelete.indexedRecordsTitle", { count: selectedRecordCount }),
                message: t("common:settings.storage.confirmDelete.indexedRecordsMessage"),
                blocked: selectedRecordCount === 0,
            },
        };

        if (config[action].blocked) {
            toast.info(t("common:settings.storage.selectAtLeastOneItemBeforeDelete"));
            return;
        }

        setDeleteConfirm({
            open: true,
            action,
            title: config[action].title,
            message: config[action].message,
        });
        setDeleteConfirmText("");
    };

    const handleDeleteConfirm = async () => {
        if (deleteConfirmText.trim().toUpperCase() !== deleteKeyword.toUpperCase() || !deleteConfirm.action) {
            return;
        }

        setDeleteConfirmLoading(true);

        try {
            switch (deleteConfirm.action) {
                case "local-all":
                    await clearLocalStorage();
                    break;
                case "local-selected":
                    await clearSelectedLocalStorage();
                    break;
                case "indexed-all":
                    await clearIndexedDb();
                    break;
                case "indexed-selected":
                    await clearSelectedIndexedDb();
                    break;
                case "indexed-stores":
                    await clearSelectedIndexedDbStores();
                    break;
                case "indexed-records":
                    await clearSelectedIndexedDbRecords();
                    break;
                default:
                    break;
            }
            closeDeleteConfirm();
        } catch {
            setDeleteConfirmLoading(false);
        }
    };

    const toggleStoreExpanded = async (dbName: string, storeName: string) => {
        const store = indexedDbItems
            .find((db) => db.name === dbName)
            ?.stores.find((s) => s.name === storeName);

        if (!store) return;

        if (store.recordsLoaded) {
            setIndexedDbItems((prev) =>
                prev.map((db) =>
                    db.name === dbName
                        ? {
                            ...db,
                            stores: db.stores.map((s) => (s.name === storeName ? { ...s, expanded: !s.expanded } : s)),
                        }
                        : db
                )
            );
            return;
        }

        setIndexedDbItems((prev) =>
            prev.map((db) =>
                db.name === dbName
                    ? {
                        ...db,
                        stores: db.stores.map((s) =>
                            s.name === storeName ? { ...s, expanded: true, recordsLoading: true } : s
                        ),
                    }
                    : db
            )
        );

        const records = await loadStoreRecords(dbName, storeName);

        setIndexedDbItems((prev) =>
            prev.map((db) =>
                db.name === dbName
                    ? {
                        ...db,
                        stores: db.stores.map((s) =>
                            s.name === storeName
                                ? {
                                    ...s,
                                    records,
                                    recordsLoaded: true,
                                    recordsLoading: false,
                                }
                                : s
                        ),
                    }
                    : db
            )
        );
    };

    const toggleAllLocalSelection = (checked: boolean) => {
        setLocalStorageItems((prev) => prev.map((item) => ({ ...item, selected: checked })));
    };

    const toggleAllIndexedSelection = (checked: boolean) => {
        setIndexedDbItems((prev) => prev.map((item) => ({ ...item, selected: checked })));
    };

    const toggleAllIndexedStoreSelection = (checked: boolean) => {
        setIndexedDbItems((prev) =>
            prev.map((item) => ({
                ...item,
                stores: item.stores.map((store) => ({ ...store, selected: checked })),
            }))
        );
    };

    const toggleAllIndexedRecordSelection = (checked: boolean) => {
        setIndexedDbItems((prev) =>
            prev.map((item) => ({
                ...item,
                stores: item.stores.map((store) => ({
                    ...store,
                    records: store.records.map((record) => ({ ...record, selected: checked })),
                })),
            }))
        );
    };

    // Do NOT call checkSession({ slide }) here — Auth already validates on bootstrap.
    // Fetch once when we have a usable session + settings read grant.
    const didFetchSettingsRef = useRef(false);

    useEffect(() => {
        if (loginModalOpen) return;
        if (!session?.access_token || !Util.isSessionValid(session)) return;

        if (canRead(access, 'settings')) {
            if (!didFetchSettingsRef.current) {
                didFetchSettingsRef.current = true;
                void fetchSettings();
            }
        } else if (accessVersion > 0) {
            navigate("/");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session?.access_token, loginModalOpen, accessVersion]);

    useEffect(() => {
        refreshStorageStats();
    }, []);

    const handleSave = async (key: string, value: string) => {
        if (!allowOrWarn(canUpdate(access, 'settings'))) return;
        const payload = { value };
        toastIdRef.current = toast.loading(t("common:settings.savingSettings"));

        try {
            const res = await api.put(`/config/${key}`, payload);
            if (res?.status === 201 || res?.status === 204) {
                toast.update(toastIdRef.current, {
                    render: t("common:toast.success.setting.save"),
                    type: "success",
                    isLoading: false,
                    autoClose: 3000,
                });
                fetchSettings();
            } else if (res?.status === 409) {
                toast.update(toastIdRef.current, {
                    render: t("common:toast.error.integrity"),
                    type: "error",
                    isLoading: false,
                    autoClose: 3000,
                });
            } else if (res.status === 401 || res.status === 422) {
                toast.dismiss(toastIdRef.current);
            } else {
                toast.update(toastIdRef.current, {
                    render: t("common:toast.error.setting.save"),
                    type: "error",
                    isLoading: false,
                    autoClose: 3000,
                });
            }
        } catch (err) {
            toast.update(toastIdRef.current, {
                render: t("common:toast.error.setting.save"),
                type: "error",
                isLoading: false,
                autoClose: 3000,
            });
        }
    };

    const handleBooleanToggle = (key: string) => {
        const newValue = (fullSettings[key]?.value || fullSettings[key]?.c_value) === "True" ? "False" : "True";
        handleSave(key, newValue);
    };

    const handleEditOpen = (item: any) => {
        setEditItem(item);
        const newValue = item.c_key.includes("TIMEOUT") ? (Number(item?.value || item?.c_value) / 60).toString() : (item?.value || item?.c_value);
        setEditValue(newValue);
        setOpenDialog(true);
    };

    const handleEdit = () => {
        const key = editItem.c_key;
        const newValue = editItem.c_key.includes("TIMEOUT") ? (Number(editValue) * 60).toString() : editValue;

        handleSave(key, newValue);
        setOpenDialog(false);
    };

    const fileToBase64 = (file: File): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });

    const editItemType = getSettingType(editItem);
    const editItemEnumOptions = getSettingEnumOptions(editItem);

    return (
        <Box
            sx={{
                height: "100%",
                width: "100%",
                background:
                    theme.palette.mode === "dark"
                        ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`
                        : `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%)`,
                display: "flex",
                flexDirection: "column",
                position: "relative",
                overflowY: "auto",
            }}
        >
            {/* Main Content */}
            <Box sx={{ flex: 1, p: { xs: 2, md: 4 }, pb: 10 }}>
                {/* Header */}
                <Fade in timeout={600}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 4, gap: 1 }}>
                        {/* Back Button */}
                        <Tooltip title={t("common:ui.tooltips.goBack")}>
                            <IconButton
                                onClick={() => navigate(-1)} // Go back in history
                                sx={{
                                    bgcolor: "action.hover",
                                    color: "text.primary",
                                    "&:hover": {
                                        bgcolor: "action.selected",
                                        transform: "translateX(-2px)",
                                    },
                                    transition: "all 0.2s ease",
                                    boxShadow: 2,
                                }}
                                size="large"
                            >
                                <ArrowBack sx={{ fontSize: 28 }} />
                            </IconButton>
                        </Tooltip>
                        <Box
                            sx={{
                                bgcolor: "primary.main",
                                color: "primary.contrastText",
                                borderRadius: "50%",
                                p: 1.5,
                                mr: 2,
                                boxShadow: 3,
                            }}
                        >
                            <SettingsIcon sx={{ fontSize: 32 }} />
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography
                                variant="h4"
                                fontWeight={700}
                                sx={{
                                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    letterSpacing: "-0.5px",
                                }}
                            >
                                {t("common:settings.appTitle")}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                                {t("common:settings.appSubtitle")}
                            </Typography>
                        </Box>
                        {/* Logout Button – Pushed to the far right */}
                        <Tooltip title={t("common:ui.tooltips.signOut")}>
                            <IconButton
                                onClick={() => { void logout(); }}
                                sx={{
                                    bgcolor: "error.main",
                                    color: "error.contrastText",
                                    "&:hover": {
                                        bgcolor: "error.dark",
                                        transform: "translateY(-1px)",
                                    },
                                    transition: "all 0.2s ease",
                                    boxShadow: 3,
                                }}
                                size="large"
                            >
                                <Logout sx={{ fontSize: 26 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>

                </Fade>

                {/* Settings Grid */}
                {!fullSettings && (settingsLoading || settingsLoadFailed) ? (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            borderRadius: 3,
                            border: `1px solid ${theme.palette.divider}`,
                            background: theme.palette.background.paper,
                            textAlign: "center",
                            mb: 3,
                        }}
                    >
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                            {settingsLoading ? t("common:settings.loadingTitle") : t("common:settings.loadFailedTitle")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {settingsLoading
                                ? t("common:settings.loadingDescription", "Fetching the latest configuration from the server.")
                                : t("common:settings.loadFailedDescription", "Settings could not be loaded. Retry once the session is ready.")}
                        </Typography>
                        <Button
                            variant={settingsLoading ? "outlined" : "contained"}
                            onClick={fetchSettings}
                            disabled={settingsLoading}
                        >
                            {settingsLoading ? t("common:settings.loadingAction") : t("common:ui.buttons.retry")}
                        </Button>
                    </Paper>
                ) : (
                    <Grid container spacing={3}>
                        {Object.entries(grouped).map(([category, items]) => (
                            <Grid size={{ xs: 12, sm: 12, md: 3 }} key={category}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        height: "100%",
                                        border: `1px solid ${theme.palette.divider}`,
                                        background: theme.palette.background.paper,
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                            boxShadow: 6,
                                            transform: "translateY(-2px)",
                                        },
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                        <Chip
                                            label={t(`common:settings.categories.${category}`)}
                                            size="small"
                                            sx={{
                                                width: "100%",
                                                bgcolor: theme.palette.primary.main + "15",
                                                color: theme.palette.primary.main,
                                                fontWeight: 600,
                                                textTransform: "uppercase",
                                                fontSize: "0.7rem",
                                                letterSpacing: "0.5px",
                                            }}
                                        />
                                    </Box>
                                    <Divider sx={{ mb: 2 }} />

                                    {items.map((item) => (
                                        <Box
                                            key={item.c_key}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                py: 2,
                                                borderBottom: `1px solid ${theme.palette.divider}`,
                                                "&:last-child": { borderBottom: "none" },
                                            }}
                                        >
                                            <Box sx={{ flex: 1 }}>
                                                <Tooltip title={`${item?.hint} \n ${t("common:tableView.lastUpdated")}: ${new Date(item.updated).toLocaleString()}`}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        fontWeight={600}
                                                        sx={{ color: "text.primary", cursor: "help" }}
                                                    >
                                                        {item.friendly_name}
                                                    </Typography>
                                                </Tooltip>

                                                {getSettingType(item) === "image" ? (
                                                    cropperData ? (
                                                        <Box sx={{ mt: 2, mb: 2 }}>
                                                            <Box
                                                                sx={{
                                                                    position: "relative",
                                                                    height: 280,
                                                                    borderRadius: 2,
                                                                    overflow: "hidden",
                                                                    boxShadow: 2,
                                                                    maxWidth: "100%",
                                                                    width: "100%",
                                                                    aspectRatio: "1 / 1",
                                                                    mx: "auto",
                                                                }}
                                                            >
                                                                <Cropper
                                                                    src={cropperData}
                                                                    style={{ height: "100%", width: "100%" }}
                                                                    initialAspectRatio={1}
                                                                    aspectRatio={1}
                                                                    guides={true}
                                                                    viewMode={1}
                                                                    autoCropArea={0.8}
                                                                    background={false}
                                                                    responsive={true}
                                                                    cropBoxResizable={true}
                                                                    dragMode="move"
                                                                    onInitialized={(instance: any) => setCropperRef(instance)}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    ) : (
                                                        <>
                                                            <Box sx={{ textAlign: "center", my: 2 }}>
                                                                <Avatar
                                                                    src={item?.value || item?.c_value}
                                                                    alt={item.c_key}
                                                                    sx={{
                                                                        width: 100,
                                                                        height: 100,
                                                                        mx: "auto",
                                                                        borderRadius: 2,
                                                                        border: `2px solid ${theme.palette.divider}`,
                                                                    }}
                                                                    variant="rounded"
                                                                />
                                                            </Box>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ display: "block", fontStyle: "italic" }}
                                                            >
                                                                {t("common:validation.imageFormats")}
                                                            </Typography>
                                                        </>
                                                    )
                                                ) : (
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.5 }}>
                                                        {getSettingType(item) === "color" && (
                                                            <Box
                                                                sx={{
                                                                    width: 24,
                                                                    height: 24,
                                                                    borderRadius: "6px",
                                                                    backgroundColor: item?.value || item?.c_value,
                                                                    border: "1px solid",
                                                                    borderColor: "divider",
                                                                    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                                                                }}
                                                            />
                                                        )}
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{
                                                                mt: 0.5,
                                                                fontFamily: theme.typography.fontFamily,
                                                            }}
                                                        >
                                                            {item.c_key.includes("TIMEOUT")
                                                                ? t("common:settings.timeoutMinutes", { count: Number(item?.value || item?.c_value) / 60 })
                                                                : item?.value || item?.c_value}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>

                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                                {getSettingType(item) === "boolean" && (
                                                    <Switch
                                                        checked={(item?.value || item?.c_value) === "True"}
                                                        onChange={() => handleBooleanToggle(item.c_key)}
                                                        color="primary"
                                                        disabled={!canEdit}
                                                        sx={{
                                                            "& .MuiSwitch-thumb": {
                                                                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                                            },
                                                        }}
                                                    />
                                                )}

                                                {getSettingType(item) === "image" && (
                                                    <>
                                                        {cropperData &&
                                                            <>
                                                                <Tooltip title={t("common:ui.tooltips.applyCrop")}>
                                                                    <IconButton
                                                                        onClick={async () => {
                                                                            if (!cropperRef) return;
                                                                            const canvas = cropperRef.getCroppedCanvas({
                                                                                width: 800,
                                                                                imageSmoothingQuality: "high",
                                                                            });
                                                                            const croppedBase64 = canvas.toDataURL("image/png", 0.9);
                                                                            setCropperData(null);
                                                                            await handleSave(item.c_key, croppedBase64);
                                                                        }}
                                                                        size="small"
                                                                        sx={{
                                                                            bgcolor: "action.hover",
                                                                            "&:hover": { bgcolor: "action.selected" },
                                                                        }}
                                                                    >
                                                                        <Check fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title={t("common:ui.buttons.cancel")}>
                                                                    <IconButton
                                                                        onClick={() => setCropperData(null)}
                                                                        size="small"
                                                                        sx={{
                                                                            bgcolor: "action.hover",
                                                                            "&:hover": { bgcolor: "action.selected" },
                                                                        }}
                                                                    >
                                                                        <CancelOutlined fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </>
                                                        }
                                                        {!cropperData && (item?.value || item?.c_value) &&
                                                            <Tooltip title={t("common:ui.tooltips.cropImage")}>
                                                                <IconButton
                                                                    onClick={() => setCropperData(item?.value || item?.c_value)}
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: "action.hover",
                                                                        "&:hover": { bgcolor: "action.selected" },
                                                                    }}
                                                                >
                                                                    <Crop fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        }
                                                        <Tooltip title={t("common:ui.tooltips.uploadNew")}>
                                                            <IconButton
                                                                component="label"
                                                                size="small"
                                                                disabled={!canEdit}
                                                                sx={{
                                                                    bgcolor: "primary.main",
                                                                    color: "white",
                                                                    "&:hover": { bgcolor: "primary.dark" },
                                                                }}
                                                            >
                                                                <AddPhotoAlternateOutlined fontSize="small" />
                                                                <input
                                                                    type="file"
                                                                    hidden
                                                                    accept="image/*"
                                                                    onChange={async (e) => {
                                                                        const file = e.target.files?.[0];
                                                                        if (!file) return;
                                                                        const MAX_MB = 2;
                                                                        if (file.size > MAX_MB * 1024 * 1024) {
                                                                            toast.error(t("common:validation.maxFileSize", { size: MAX_MB }));
                                                                            return;
                                                                        }
                                                                        const base64 = await fileToBase64(file);
                                                                        setCropperData(base64);
                                                                    }}
                                                                />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )}

                                                {(["string", "number", "enum", "color"].includes(getSettingType(item))) && (
                                                    <Tooltip title={t("common:ui.tooltips.editValue")}>
                                                        <IconButton
                                                            onClick={() => handleEditOpen(item)}
                                                            size="small"
                                                            disabled={!canEdit}
                                                            sx={{
                                                                bgcolor: "action.hover",
                                                                "&:hover": { bgcolor: "action.selected" },
                                                            }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Box>
                                    ))}
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                )}

                <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.divider}`,
                                background: theme.palette.background.paper,
                            }}
                        >
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}>
                                <Typography variant="h6" fontWeight={700}>
                                    {t("common:settings.storage.title")}
                                </Typography>
                                <Button variant="outlined" onClick={refreshStorageStats} disabled={storageLoading}>
                                    {storageLoading ? t("common:settings.storage.refreshing") : t("common:settings.storage.refreshSizes")}
                                </Button>
                            </Box>

                            <Divider sx={{ mb: 2 }} />

                            <Box
                                sx={{
                                    mb: 2,
                                    p: 2,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.error.main}`,
                                    background: `linear-gradient(135deg, ${theme.palette.error.main}12 0%, ${theme.palette.error.main}08 100%)`,
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                                    <WarningAmberRoundedIcon sx={{ color: "error.main", mt: 0.2 }} />
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={800} color="error.main" sx={{ mb: 0.5 }}>
                                            {t("common:settings.storage.dangerTitle")}
                                        </Typography>
                                        <Typography variant="body2" color="text.primary">
                                            {t("common:settings.storage.dangerDescription")}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Accordion
                                expanded={expandedStorage.local}
                                onChange={(_, expanded) => setExpandedStorage((prev) => ({ ...prev, local: expanded }))}
                                disableGutters
                                elevation={0}
                                sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, mb: 2, "&:before": { display: "none" } }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", pr: 1 }}>
                                        <Typography variant="subtitle1" fontWeight={700}>{t("common:settings.storage.pendingData")}</Typography>
                                        <Typography variant="body2" color="text.secondary">{formatBytes(localStorageBytes)}</Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
                                        <Button variant="outlined" size="small" onClick={() => toggleAllLocalSelection(true)}>{t("common:settings.storage.selectAll")}</Button>
                                        <Button variant="outlined" size="small" onClick={() => toggleAllLocalSelection(false)}>{t("common:settings.storage.clearSelection")}</Button>
                                        <Button variant="outlined" size="small" startIcon={<DeleteForeverIcon />} sx={dangerOutlineButtonSx} onClick={() => openDeleteConfirm("local-selected")}>{t("common:settings.storage.clearSelected")}</Button>
                                        <Button variant="contained" size="small" startIcon={<DeleteForeverIcon />} sx={dangerContainedButtonSx} onClick={() => openDeleteConfirm("local-all")}>{t("common:settings.storage.clearAll")}</Button>
                                    </Box>

                                    <Box sx={{ maxHeight: 220, overflowY: "auto", pr: 0.5 }}>
                                        {localStorageItems.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">{t("common:settings.storage.noPendingData")}</Typography>
                                        ) : (
                                            localStorageItems.map((item) => (
                                                <Box key={item.key} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                                                        <Checkbox
                                                            size="small"
                                                            checked={item.selected}
                                                            onChange={(e) => {
                                                                const checked = e.target.checked;
                                                                setLocalStorageItems((prev) => prev.map((it) => (it.key === item.key ? { ...it, selected: checked } : it)));
                                                            }}
                                                        />
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography variant="body2" fontWeight={500} sx={{ wordBreak: "break-all" }}>{item.label}</Typography>
                                                        </Box>
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary" sx={{ pl: 1, whiteSpace: "nowrap" }}>{formatBytes(item.size)}</Typography>
                                                </Box>
                                            ))
                                        )}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>

                            <Accordion
                                expanded={expandedStorage.indexed}
                                onChange={(_, expanded) => setExpandedStorage((prev) => ({ ...prev, indexed: expanded }))}
                                disableGutters
                                elevation={0}
                                sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, "&:before": { display: "none" } }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", pr: 1 }}>
                                        <Typography variant="subtitle1" fontWeight={700}>{t("common:settings.storage.cachedData")}</Typography>
                                        <Typography variant="body2" color="text.secondary">{indexedDbBytes === null ? t("common:settings.storage.unavailable") : formatBytes(indexedDbBytes)}</Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                                        <Button variant="outlined" size="small" onClick={() => toggleAllIndexedSelection(true)}>{t("common:settings.storage.selectAll")}</Button>
                                        <Button variant="outlined" size="small" onClick={() => toggleAllIndexedSelection(false)}>{t("common:settings.storage.clearSelection")}</Button>
                                        <Button variant="outlined" size="small" startIcon={<DeleteForeverIcon />} sx={dangerOutlineButtonSx} onClick={() => openDeleteConfirm("indexed-selected")}>{t("common:settings.storage.clearSelected")}</Button>
                                        <Button variant="contained" size="small" startIcon={<DeleteForeverIcon />} sx={dangerContainedButtonSx} onClick={() => openDeleteConfirm("indexed-all")}>{t("common:settings.storage.clearAll")}</Button>
                                    </Box>

                                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
                                        <Button variant="outlined" size="small" onClick={() => toggleAllIndexedStoreSelection(true)}>{t("common:settings.storage.selectAllStores")}</Button>
                                        <Button variant="outlined" size="small" onClick={() => toggleAllIndexedStoreSelection(false)}>{t("common:settings.storage.clearStoreSelection")}</Button>
                                        <Button variant="outlined" size="small" startIcon={<DeleteForeverIcon />} sx={dangerOutlineButtonSx} onClick={() => openDeleteConfirm("indexed-stores")}>{t("common:settings.storage.clearSelectedStores")}</Button>
                                    </Box>

                                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
                                        <Button variant="outlined" size="small" onClick={() => toggleAllIndexedRecordSelection(true)}>{t("common:settings.storage.selectAllRecords")}</Button>
                                        <Button variant="outlined" size="small" onClick={() => toggleAllIndexedRecordSelection(false)}>{t("common:settings.storage.clearRecordSelection")}</Button>
                                        <Button variant="outlined" size="small" startIcon={<DeleteForeverIcon />} sx={dangerOutlineButtonSx} onClick={() => openDeleteConfirm("indexed-records")}>{t("common:settings.storage.clearSelectedRecords")}</Button>
                                    </Box>

                                    <Box sx={{ maxHeight: 220, overflowY: "auto", pr: 0.5 }}>
                                        {indexedDbItems.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">{t("common:settings.storage.noCachedData")}</Typography>
                                        ) : (
                                            indexedDbItems.map((item) => (
                                                <Box key={item.name} sx={{ py: 0.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                                        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                                                            <Checkbox
                                                                size="small"
                                                                checked={item.selected}
                                                                onChange={(e) => {
                                                                    const checked = e.target.checked;
                                                                    setIndexedDbItems((prev) => prev.map((it) => (it.name === item.name ? { ...it, selected: checked } : it)));
                                                                }}
                                                            />
                                                            <Typography variant="body2" sx={{ wordBreak: "break-all" }}>{item.name}</Typography>
                                                        </Box>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                            <Typography variant="caption" color="text.secondary" sx={{ pl: 1, whiteSpace: "nowrap" }}>{formatBytes(item.size)}</Typography>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => {
                                                                    setIndexedDbItems((prev) =>
                                                                        prev.map((it) => (it.name === item.name ? { ...it, expanded: !it.expanded } : it))
                                                                    );
                                                                }}
                                                            >
                                                                <ExpandMoreIcon
                                                                    fontSize="small"
                                                                    sx={{
                                                                        transform: item.expanded ? "rotate(180deg)" : "rotate(0deg)",
                                                                        transition: "transform 0.2s ease",
                                                                    }}
                                                                />
                                                            </IconButton>
                                                        </Box>
                                                    </Box>

                                                    {item.expanded && (
                                                        <Box sx={{ pl: 4, pt: 0.5 }}>
                                                            {item.stores.length === 0 ? (
                                                                <Typography variant="caption" color="text.secondary">{t("common:settings.storage.noObjectStores")}</Typography>
                                                            ) : (
                                                                item.stores.map((store) => (
                                                                    <Box key={`${item.name}-${store.name}`} sx={{ py: 0.25 }}>
                                                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                                                            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                                                                                <Checkbox
                                                                                    size="small"
                                                                                    checked={store.selected}
                                                                                    onChange={(e) => {
                                                                                        const checked = e.target.checked;
                                                                                        setIndexedDbItems((prev) =>
                                                                                            prev.map((it) =>
                                                                                                it.name === item.name
                                                                                                    ? {
                                                                                                        ...it,
                                                                                                        stores: it.stores.map((s) =>
                                                                                                            s.name === store.name ? { ...s, selected: checked } : s
                                                                                                        ),
                                                                                                    }
                                                                                                    : it
                                                                                            )
                                                                                        );
                                                                                    }}
                                                                                />
                                                                                <Typography variant="caption" sx={{ wordBreak: "break-all" }}>{store.name}</Typography>
                                                                            </Box>
                                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                                                <Typography variant="caption" color="text.secondary" sx={{ pl: 1, whiteSpace: "nowrap" }}>{formatBytes(store.size)}</Typography>
                                                                                <IconButton size="small" onClick={() => toggleStoreExpanded(item.name, store.name)}>
                                                                                    <ExpandMoreIcon
                                                                                        fontSize="small"
                                                                                        sx={{
                                                                                            transform: store.expanded ? "rotate(180deg)" : "rotate(0deg)",
                                                                                            transition: "transform 0.2s ease",
                                                                                        }}
                                                                                    />
                                                                                </IconButton>
                                                                            </Box>
                                                                        </Box>

                                                                        {store.expanded && (
                                                                            <Box sx={{ pl: 4, py: 0.25 }}>
                                                                                {store.recordsLoading && (
                                                                                    <Typography variant="caption" color="text.secondary">{t("common:settings.storage.loadingRecords")}</Typography>
                                                                                )}

                                                                                {!store.recordsLoading && store.recordsLoaded && store.records.length === 0 && (
                                                                                    <Typography variant="caption" color="text.secondary">{t("common:settings.storage.noRecordsInStore")}</Typography>
                                                                                )}

                                                                                {!store.recordsLoading && store.records.length > 0 && (
                                                                                    <>
                                                                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
                                                                                            {t("common:settings.storage.showingUpToRecords", { count: MAX_RECORDS_PER_STORE })}
                                                                                        </Typography>
                                                                                        {store.records.map((record) => (
                                                                                            <Box key={`${item.name}-${store.name}-${record.keyLabel}`} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                                                                                <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                                                                                                    <Checkbox
                                                                                                        size="small"
                                                                                                        checked={record.selected}
                                                                                                        onChange={(e) => {
                                                                                                            const checked = e.target.checked;
                                                                                                            setIndexedDbItems((prev) =>
                                                                                                                prev.map((it) =>
                                                                                                                    it.name === item.name
                                                                                                                        ? {
                                                                                                                            ...it,
                                                                                                                            stores: it.stores.map((s) =>
                                                                                                                                s.name === store.name
                                                                                                                                    ? {
                                                                                                                                        ...s,
                                                                                                                                        records: s.records.map((r) =>
                                                                                                                                            r.keyLabel === record.keyLabel ? { ...r, selected: checked } : r
                                                                                                                                        ),
                                                                                                                                    }
                                                                                                                                    : s
                                                                                                                            ),
                                                                                                                        }
                                                                                                                        : it
                                                                                                                )
                                                                                                            );
                                                                                                        }}
                                                                                                    />
                                                                                                    <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
                                                                                                        {record.keyLabel}
                                                                                                    </Typography>
                                                                                                </Box>
                                                                                                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>{formatBytes(record.size)}</Typography>
                                                                                            </Box>
                                                                                        ))}
                                                                                    </>
                                                                                )}
                                                                            </Box>
                                                                        )}
                                                                    </Box>
                                                                ))
                                                            )}
                                                        </Box>
                                                    )}
                                                </Box>
                                            ))
                                        )}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    position: "relative",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    bgcolor: "background.paper",
                    color: "text.secondary",
                    py: 2.5,
                    px: 4,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    fontSize: "0.875rem",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "text.primary" }}>
                        <Link href="https://moleculesoft.net" target="_blank"
                            rel="noopener noreferrer">MOLeCULE Soft</Link>
                    </Typography>
                    <Typography variant="caption">© {COPYRIGHT_YEAR} {t("common:settings.allRightsReserved")}</Typography>
                </Box>
                <Typography variant="caption" sx={{ color: "text.disabled" }}>
                    {t("common:settings.storage.systemVersion")} <strong style={{ color: theme.palette.text.primary }}>{APP_VERSION}</strong>
                </Typography>
            </Box>

            {/* Edit Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>{t("common:dialog.editConfiguration")}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="subtitle2" sx={{ mb: 2, color: "text.secondary" }}>
                        {editItem?.friendly_name}
                    </Typography>

                    {(editItemType === "string" || editItemType === "number") && (
                        <TextField
                            fullWidth
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            label={t("common:ui.labels.value")}
                            variant="outlined"
                            required
                            {...(editItemType === "number" && { type: "number", inputProps: { min: 1, max: 30 } })}
                            {...(editItemType === "string" && { inputProps: { maxLength: MYSQL_LIMITS.TEXT.maxLength } })}
                            sx={{ mt: 1 }}
                        />
                    )}

                    {editItemType === "enum" && (
                        <Autocomplete
                            options={editItemEnumOptions}
                            value={editValue}
                            onChange={(_e, newVal) => setEditValue(newVal ?? "")}
                            renderInput={(params) => (
                                <TextField {...params} label={t("common:ui.labels.selectOption")} variant="outlined" required />
                            )}
                            disableClearable
                            sx={{ mt: 1 }}
                        />
                    )}


                    {(editItemType === "color") && (
                        <ColorPicker
                            value={editValue}
                            onChange={setEditValue}
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenDialog(false)} startIcon={<CloseIcon />}>
                        {t("common:ui.buttons.cancel")}
                    </Button>
                    <Button variant="contained" onClick={handleEdit} startIcon={<SaveIcon />} disabled={!editValue.trim()}>
                        {t("common:ui.buttons.save")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteConfirm.open} onClose={deleteConfirmLoading ? undefined : closeDeleteConfirm} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        fontWeight: 700,
                        color: "error.main",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        borderBottom: `1px solid ${theme.palette.error.main}33`,
                    }}
                >
                    <WarningAmberRoundedIcon color="error" />
                    {deleteConfirm.title}
                </DialogTitle>
                <DialogContent dividers sx={{ pt: 2.5 }}>
                    <Box
                        sx={{
                            p: 2,
                            mb: 2,
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.error.main}`,
                            backgroundColor: `${theme.palette.error.main}0d`,
                        }}
                    >
                        <Typography variant="body2" sx={{ mb: 1, color: "text.primary" }}>
                            {deleteConfirm.message}
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="error.main">
                            {t("common:settings.storage.confirmDelete.actionCannotBeUndone")}
                        </Typography>
                    </Box>

                    <Typography variant="body2" sx={{ mb: 1 }}>
                        {t("common:settings.storage.confirmDelete.typeDeleteToConfirm")} <strong>{deleteKeyword}</strong>.
                    </Typography>
                    <TextField
                        fullWidth
                        autoFocus
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        placeholder={t("common:settings.storage.confirmDelete.deleteKeyword")}
                        label={t("common:settings.storage.confirmDelete.confirmation")}
                        error={deleteConfirmText.length > 0 && deleteConfirmText.trim().toUpperCase() !== deleteKeyword.toUpperCase()}
                        helperText={deleteConfirmText.length > 0 && deleteConfirmText.trim().toUpperCase() !== deleteKeyword.toUpperCase() ? t("common:settings.storage.confirmDelete.enterDeleteExactly") : " "}
                        disabled={deleteConfirmLoading}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
                    <Button onClick={closeDeleteConfirm} startIcon={<CloseIcon />} disabled={deleteConfirmLoading}>
                        {t("common:ui.buttons.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<DeleteForeverIcon />}
                        onClick={handleDeleteConfirm}
                        disabled={deleteConfirmLoading || deleteConfirmText.trim().toUpperCase() !== deleteKeyword.toUpperCase()}
                        sx={{ minWidth: 190, fontWeight: 700 }}
                    >
                        {deleteConfirmLoading ? t("common:settings.storage.confirmDelete.deleting") : t("common:settings.storage.confirmDelete.deletePermanently")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminSettings;